/* Accessibility fix: WCAG 2.4.3 Focus Order (FAQ category tabs)
   The tab-switching logic here already follows the standard ARIA tabs
   pattern well (role="tab"/"tabpanel", aria-selected, roving tabindex,
   arrow-key navigation) — better than most components in this theme.
   The one gap: after switching tabs, focus stays on the tab button itself,
   so a keyboard user has to press Tab an extra time to reach the new
   panel's content, with no indication of where that lands. This adds
   tabindex="-1" to each panel (so it can receive programmatic focus
   without being in the normal Tab sequence) and moves focus onto the
   newly active panel right after switching, so users land directly in
   the new content instead of needing an extra step to find it.

   Accessibility fix: WCAG 2.4.3 Focus Order / 2.4.7 (hidden FAQ panels)
   Inactive tab panels are collapsed to zero height and marked
   aria-hidden="true", but the question buttons inside them stayed in the
   Tab order: a keyboard user pressed Tab 48 times through invisible
   questions before reaching "Contact Us" (live test, Oct 8 2026).
   Fix: inactive panels get the `inert` attribute, which removes them and
   everything inside them from both the Tab order and the accessibility
   tree without changing the layout. It's set on load and toggled every
   time a tab is selected. */
document.addEventListener("DOMContentLoaded", () => {
  const faqSections = document.querySelectorAll(".faq[data-faq-section]");

  faqSections.forEach((faqSection) => {
    const tabs = faqSection.querySelectorAll(".faq-section__tab");
    const groups = faqSection.querySelectorAll(".faq-section__group");
    const questions = faqSection.querySelectorAll(".faq-section__question");
    const answers = faqSection.querySelectorAll(".faq-section__answer-inner");

    groups.forEach((group) => {
      if (!group.hasAttribute("tabindex")) group.setAttribute("tabindex", "-1");
      // Only tabbed FAQs have hidden panels; leave single-group FAQs alone.
      if (tabs.length > 0) {
        const isActive = group.classList.contains("faq-section__group--active");
        group.inert = !isActive;
      }
    });

    const closeAllAnswers = () => {
      questions.forEach((question) => question.setAttribute("aria-expanded", "false"));
      answers.forEach((answer) => {
        answer.style.maxHeight = null;
      });
    };

    const activateTab = (targetTab, moveFocusToPanel) => {
      const targetGroup = targetTab.dataset.tab;
      let targetGroupEl = null;

      tabs.forEach((tab) => {
        const isActive = tab === targetTab;
        tab.classList.toggle("faq-section__tab--active", isActive);
        tab.setAttribute("aria-selected", String(isActive));
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      groups.forEach((group) => {
        const isActive = group.dataset.group === targetGroup;
        group.classList.toggle("faq-section__group--active", isActive);
        group.setAttribute("aria-hidden", String(!isActive));
        group.inert = !isActive;
        if (isActive) targetGroupEl = group;
      });

      closeAllAnswers();

      if (moveFocusToPanel && targetGroupEl) {
        window.setTimeout(() => {
          targetGroupEl.focus({ preventScroll: true });
        }, 50);
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => {
        activateTab(tab, true);
      });

      tab.addEventListener("keydown", (event) => {
        const key = event.key;
        if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(key)) return;

        event.preventDefault();

        let nextIndex = index;
        if (key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (key === "Home") nextIndex = 0;
        if (key === "End") nextIndex = tabs.length - 1;

        const nextTab = tabs[nextIndex];
        if (!nextTab) return;
        activateTab(nextTab, false);
        nextTab.focus();
      });
    });

    questions.forEach((question) => {
      question.addEventListener("click", () => {
        const answer = question.nextElementSibling;
        if (!answer) return;

        const isExpanded = question.getAttribute("aria-expanded") === "true";
        closeAllAnswers();

        if (!isExpanded) {
          question.setAttribute("aria-expanded", "true");
          answer.style.maxHeight = `${answer.scrollHeight}px`;
        }
      });
    });
  });
});
